import './CurrencySelector.scss';

import { Content, Icon, Image, ListCell, ListView, Radio, RadioGroup, TextField, Typography } from '@modulor/react';
import { useSkeletonTranslation } from 'hooks/useSkeletonTranslation';
import debounce from 'lodash.debounce';
import { useNavigateWithSearch } from 'Modulor/hooks';
import { useAppCore } from 'providers/AppCoreProvider/AppCoreContext';
import { CURRENCY_CHANGE_REASON } from 'providers/AppCoreProvider/AppCoreProvider';
import type { FormEvent } from 'react';
import { useRef } from 'react';
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getClickStreamEvent, getEventAnalytic, useGetClickStreamCashierContext } from 'services/clickstream';
import type { FlowTypes } from 'store/constants';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { clearBonuses, clearSelectedBonus, selectedBonusSelector } from 'store/slices/bonus';
import { paymentFlowSelector } from 'store/slices/global';
import type { CurrencyBase, CurrencyType } from 'store/slices/wallet';
import {
  cryptoCurrenciesSelector,
  fiatCurrenciesSelector,
  onRampCurrenciesSelector,
  selectedCurrencySelector,
  walletCurrenciesSelector,
} from 'store/slices/wallet';
import { internalNavigateBack } from 'utils/navigationControl';
import { useGetSessionId } from 'utils/session';

import { useSetHeaderConfig } from '../../components/Header/hooks/useSetHeaderConfig';
import { PaymentType } from '../types';

const MIN_OPTIONS_COUNT_FOR_SEARCH_FIELD = 10;

const clickstream = getEventAnalytic();

export const CurrencySelector = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigateWithSearch();
  const { translate, skeletonT } = useSkeletonTranslation();
  const { handleCurrencyChange } = useAppCore();
  const sessionId = useGetSessionId();
  const flowType = useAppSelector(paymentFlowSelector);
  const selectedBonus = useAppSelector(selectedBonusSelector);
  const cashierContext = useGetClickStreamCashierContext(flowType);

  const location = useLocation() as {
    state?: {
      mode?: CurrencyType;
      background?: Location;
      flow?: FlowTypes;
    };
  };
  const mode: CurrencyType = location.state?.mode || PaymentType.FIAT;
  const [searchValue, setSearchValue] = useState('');

  const currentCurrency = useAppSelector(selectedCurrencySelector);

  const handleCloseWithoutSelection = () => {
    navigate(-1);
  };

  useSetHeaderConfig({
    preset: 'selectCurrency',
    onNavigationAction: handleCloseWithoutSelection,
  });

  const fiatCurrencies = useAppSelector(fiatCurrenciesSelector);
  const cryptoCurrencies = useAppSelector(cryptoCurrenciesSelector);
  const onRampCurrencies = useAppSelector(onRampCurrenciesSelector);
  const walletCurrencies = useAppSelector(walletCurrenciesSelector);

  const currenciesOptions = useMemo(() => {
    if (mode === PaymentType.FIAT) {
      return fiatCurrencies;
    }

    if (mode === PaymentType.CRYPTO) {
      return cryptoCurrencies;
    }

    if (mode === PaymentType.ON_RAMP) {
      return onRampCurrencies;
    }

    return walletCurrencies;
  }, [walletCurrencies, fiatCurrencies, cryptoCurrencies, onRampCurrencies, mode]);

  const displaySearchField = currenciesOptions.length >= MIN_OPTIONS_COUNT_FOR_SEARCH_FIELD;

  const filteredCurrencies = useMemo(
    () => currenciesOptions.filter((currency) => currency.alpha3.toLowerCase().includes(searchValue.toLowerCase())),
    [currenciesOptions, searchValue],
  );

  const cashierContextRef = useRef(cashierContext);
  cashierContextRef.current = cashierContext;

  const debouncedSearchEvent = useMemo(
    () =>
      debounce((value: string) => {
        const event = getClickStreamEvent.pw_currency_search({
          search_query: value,
        });
        clickstream.push(event, [cashierContextRef.current]);
      }, 800),
    [],
  );

  const handleSearch = (value: string | FormEvent<HTMLDivElement>) => {
    if (typeof value === 'string') {
      debouncedSearchEvent(value);
      setSearchValue(value);
    }
  };

  const handleSelectCurrency = (currency: CurrencyBase) => {
    const isChanged = currentCurrency?.id !== currency.id;
    const isCryptoFlow = mode === PaymentType.CRYPTO;
    const isOnRampFlow = mode === PaymentType.ON_RAMP;

    if (isChanged) {
      const event = getClickStreamEvent.pw_currency_change({
        sessionId,
        currency: currency.alpha3,
      });
      clickstream.push(event, [cashierContext]);

      if (selectedBonus?.withoutBonus) {
        dispatch(clearBonuses());
        dispatch(clearSelectedBonus());
      }

      handleCurrencyChange({
        currency,
        isCrypto: isCryptoFlow,
        isOnRamp: isOnRampFlow,
        tab: mode,
        reason: CURRENCY_CHANGE_REASON.CURRENCY_SELECT,
      });
      navigate(`/${flowType}`, {
        replace: true,
      });
    } else {
      internalNavigateBack(navigate);
    }
  };

  return (
    <>
      {displaySearchField && (
        <div className="search-currency">
          <TextField
            placeholder={translate('PH.CURRENCY.SEARCH.PLACEHOLDER')}
            type="text"
            value={searchValue}
            onChange={handleSearch}
            leftIcon="search"
          />
        </div>
      )}
      {!filteredCurrencies.length && (
        <div className="currency-selector-list empty-list-block">
          <Icon name="search" size={40} className="empty-list-block__icon" />
          <div className="empty-list-block__text">
            <Typography variant="title-2-semibold">{skeletonT('PH.LISTCELL.EMPTY.TITLE')}</Typography>
          </div>
        </div>
      )}
      <ListView className="currency-selector-list" key={searchValue}>
        <RadioGroup onChange={() => {}}>
          {filteredCurrencies.map((currency) => (
            <ListCell
              key={currency.id}
              className="currency-selector-list-cell list-cell-control"
              text={currency.alpha3}
              icon={<Image src={currency.iconUrl} alt={currency.alpha3} decoding="async" loading="lazy" />}
              onClick={() => handleSelectCurrency(currency)}
              content={
                (
                  <Content>
                    <Radio value={String(currency.id)} checked={currency.alpha3 === currentCurrency?.alpha3} />
                  </Content>
                ) as unknown as string
              }
            />
          ))}
        </RadioGroup>
      </ListView>
    </>
  );
};
